import { lazy, Suspense } from 'react'
import { createBrowserRouter, type RouteObject, Navigate } from 'react-router'
import { AppShell } from './components/layout/AppShell.js'
// Epic 48 Phase 02B — the flag-ON global shell (DESIGN-HANDOVER.md §8.1). Eagerly imported, not
// lazy: it IS the layout, so deferring it would flash a blank frame on every cold load.
import { WorkspaceAppShell } from './components/layout/WorkspaceAppShell.js'
import { WORKSPACE_SHELL_ENABLED } from './lib/feature-flags.js'

const LoginPage = lazy(() => import('./pages/LoginPage.js'))
const CampaignListPage = lazy(() => import('./pages/campaigns/CampaignListPage.js'))
const CampaignDashboardPage = lazy(() => import('./pages/campaigns/CampaignDashboardPage.js'))
// Epic 48 Phase 02, D-CUTOVER-1 — only ever imported when the coexistence flag is ON (see
// `buildRoutes` below), so a flag-OFF build never even fetches this chunk.
const WorkspacePage = lazy(() => import('./pages/workspace/WorkspacePage.js'))
// Same D-CUTOVER-1 coexistence flag, same reasoning, for the `campaigns` list route — the
// gap this file's Phase 11 fix closes (Phase 03 task 3.4 claimed this route was already
// flag-gated; it was not).
const CampaignTablePage = lazy(() => import('./pages/campaigns/CampaignTablePage.js'))
// The honest destination for the four Figma nav rows whose screens are undrawn. Flag-ON only.
const NotBuiltYetPage = lazy(() => import('./pages/NotBuiltYetPage.js'))
const ReviewPage = lazy(() => import('./pages/review/ReviewPage.js'))
const DeliverPage = lazy(() => import('./pages/deliver/DeliverPage.js'))
const NotFoundPage = lazy(() => import('./pages/NotFoundPage.js'))

function LazyFallback() {
  return (
    <div className="flex h-full items-center justify-center">
      <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-gray-900" />
    </div>
  )
}

function Lazy({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<LazyFallback />}>{children}</Suspense>
}

/**
 * The `campaigns/:id` route object, isolated so a test can build it for either flag state
 * without touching real `import.meta.env` — Epic 48 Phase 02 task 2.1 (D-CUTOVER-1).
 *
 * Flag OFF: exactly the route that shipped before this phase — `CampaignDashboardPage`,
 * byte-for-byte. Flag ON: the SAME path swaps to the new `WorkspacePage` shell instead. There
 * is never a second `campaigns/:id`-shaped path added anywhere — "the route is not registered
 * at all" (this phase's own acceptance wording) means the workspace-shell-backed route object
 * below is never the one built when the flag is off, not that a distinct URL exists.
 */
function campaignRoute(workspaceShellEnabled: boolean): RouteObject {
  return {
    path: 'campaigns/:id',
    element: <Lazy>{workspaceShellEnabled ? <WorkspacePage /> : <CampaignDashboardPage />}</Lazy>,
  }
}

/**
 * The `campaigns` (list) route object, isolated for the same reason as `campaignRoute` above
 * and gated on the SAME `workspaceShellEnabled` flag. Flag OFF: exactly the route that shipped
 * before this fix — `CampaignListPage`, byte-for-byte. Flag ON: the SAME path swaps to the new
 * `CampaignTablePage` (Epic 48 Phase 03's `CampaignTable` + `CreateCampaignModal`) instead.
 * There is never a second `campaigns`-shaped path added anywhere.
 */
function campaignsListRoute(workspaceShellEnabled: boolean): RouteObject {
  return {
    path: 'campaigns',
    element: <Lazy>{workspaceShellEnabled ? <CampaignTablePage /> : <CampaignListPage />}</Lazy>,
  }
}

/**
 * The four Figma nav destinations that have no screen drawn anywhere (§8.1, D-SHELL-VISIBLE-1).
 * Registered ONLY when the flag is on — a flag-OFF build has no `/admin`-shaped path at all.
 * Each lands on `NotBuiltYetPage`, which states which destination is missing and nothing else.
 */
const PARKED_DESTINATIONS = ['dashboard', 'brands', 'trash', 'admin'] as const

export function buildRoutes(workspaceShellEnabled: boolean): RouteObject[] {
  return [
    {
      path: '/',
      // The SHELL ITSELF is flag-branched, which nothing did before: `buildRoutes` parameterised
      // only the two child routes while the wrapping element stayed an unconditional `AppShell`,
      // so the new sidebar could never render no matter what the flag said. Flag OFF is still
      // byte-for-byte the shipped `AppShell`.
      element: workspaceShellEnabled ? <WorkspaceAppShell /> : <AppShell />,
      children: [
        { index: true, element: <Navigate to="/campaigns" replace /> },
        campaignsListRoute(workspaceShellEnabled),
        campaignRoute(workspaceShellEnabled),
        ...(workspaceShellEnabled
          ? PARKED_DESTINATIONS.map((path) => ({
              path,
              element: (
                <Lazy>
                  <NotBuiltYetPage />
                </Lazy>
              ),
            }))
          : []),
      ],
    },
    {
      path: 'login',
      element: (
        <Lazy>
          <LoginPage />
        </Lazy>
      ),
    },
    {
      path: 'review/:token',
      element: (
        <Lazy>
          <ReviewPage />
        </Lazy>
      ),
    },
    {
      path: 'deliver/:token',
      element: (
        <Lazy>
          <DeliverPage />
        </Lazy>
      ),
    },
    {
      path: '*',
      element: (
        <Lazy>
          <NotFoundPage />
        </Lazy>
      ),
    },
  ]
}

export const router = createBrowserRouter(buildRoutes(WORKSPACE_SHELL_ENABLED))
